import { filterByCategory } from "@/shared/constants/estateCategories";

import type { FlowScreenConfig } from "@/shared/constants/flowTypes";
import type { ScreenFactoryContext } from "../../taskScreenRegistry";

import {
  getAddMortgageForScreen,
  getExtractedMortgageComponent,
} from "../components/createPropertyScreenComponents";
import {
  getLoansQuestionComponent,
  getMortgageComponent,
  getHELOCComponent,
} from "../../real-estate/components/createPerPropertyComponents";
import { SecuredDebtsReview } from "../components/SecuredDebtsReview";

export function createMortgageScreens(
  firstName: string,
  context?: ScreenFactoryContext,
): FlowScreenConfig[] {
  const mortgages = filterByCategory(context?.debts ?? [], "Secured debts");
  const properties = filterByCategory(context?.assets ?? [], "Properties");

  if (mortgages.length > 0) {
    return [
      {
        id: "secured_debts_review",
        layout: "full",
        textAlign: "center",
        maxWidth: "2xl",
        title: `Review ${firstName}'s secured debts`,
        subtitle: "Review the details and edit anything that looks off.",
        hideButton: true,
        customComponent: SecuredDebtsReview,
      },
    ];
  }

  const screens: FlowScreenConfig[] = [];

  const mortgageCount = Math.max(mortgages.length, 1);
  for (let i = 0; i < mortgageCount; i++) {
    const mortgage = mortgages[i] ?? null;
    const isFirst = i === 0;
    const screenId = isFirst ? "extracted_mortgages" : `extracted_mortgage_${mortgage?.id}`;
    const addScreenId = isFirst ? "add_mortgage" : `add_mortgage_extracted_${mortgage?.id}`;

    const sourceDocId = mortgage?.source_document_id;
    const source = sourceDocId
      ? (context?.documentTypeLabels[sourceDocId] ?? "documents")
      : "documents";
    const counter = mortgages.length > 1 ? ` (${i + 1} of ${mortgages.length})` : "";

    screens.push({
      id: screenId,
      layout: "full",
      textAlign: "center",
      title: `We found a mortgage for ${firstName}${counter}`,
      subtitle: `Based on recurring payments from ${firstName}'s ${source.toLowerCase()}.`,
      contentComponent: getExtractedMortgageComponent(mortgage?.id ?? null, isFirst),
      question: {
        type: "multiple_choice",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
          { value: "not_sure", label: "I'm not sure" },
        ],
      },
      shouldDisplay: ({ answers }) => answers["extracted_mortgages"] !== "none",
    });

    screens.push({
      id: addScreenId,
      layout: "full",
      textAlign: "center",
      title: "Here's what we have so far",
      subtitle: "Add what you know. We'll help you gather details later.",
      hideButton: true,
      skipOnBack: true,
      customComponent: getAddMortgageForScreen(addScreenId),
      shouldDisplay: ({ answers }) => answers[screenId] === "yes",
    });
  }

  screens.push({
    id: "has_additional_mortgage",
    layout: "full",
    textAlign: "center",
    title: `Did ${firstName} have any other mortgages?`,
    question: {
      type: "multiple_choice",
      required: true,
      options: [
        { value: "yes", label: "Yes, add another mortgage" },
        { value: "no", label: "No other mortgages" },
      ],
    },
    shouldDisplay: ({ answers }) => answers["extracted_mortgages"] !== "none",
  });

  screens.push({
    id: "add_additional_mortgage",
    layout: "full",
    textAlign: "center",
    title: "Add mortgage information",
    subtitle: "Add what you know. We'll help you gather details later.",
    hideButton: true,
    skipOnBack: true,
    customComponent: getAddMortgageForScreen(
      "add_additional_mortgage",
      undefined,
      true,
      undefined,
      "has_additional_mortgage",
    ),
    shouldDisplay: ({ answers }) => answers["has_additional_mortgage"] === "yes",
  });

  for (let i = 0; i < properties.length; i++) {
    const pid = properties[i].id;

    screens.push(
      {
        id: `property_loans_question_${pid}`,
        layout: "full",
        maxWidth: "2xl",
        hideButton: true,
        customComponent: getLoansQuestionComponent(pid),
        shouldDisplay: ({ answers }) => answers["extracted_mortgages"] === "none",
      },
      {
        id: `add_mortgage_${pid}`,
        layout: "full",
        maxWidth: "4xl",
        hideButton: true,
        customComponent: getMortgageComponent(pid),
        shouldDisplay: ({ answers }) =>
          answers["extracted_mortgages"] === "none" && answers[`has_mortgage_${pid}`] === "yes",
      },
      {
        id: `add_heloc_${pid}`,
        layout: "full",
        maxWidth: "4xl",
        hideButton: true,
        customComponent: getHELOCComponent(pid),
        shouldDisplay: ({ answers }) =>
          answers["extracted_mortgages"] === "none" && answers[`has_heloc_${pid}`] === "yes",
      },
    );
  }

  if (properties.length === 0) {
    screens.push(
      {
        id: "has_mortgage_no_property",
        layout: "full",
        textAlign: "center",
        title: `Did ${firstName} have any mortgages?`,
        question: {
          type: "multiple_choice",
          required: true,
          options: [
            { value: "yes", label: "Yes, there was a mortgage" },
            { value: "no", label: "No mortgages" },
            { value: "not_sure", label: "I'm not sure" },
          ],
        },
        shouldDisplay: ({ answers }) => answers["extracted_mortgages"] === "none",
      },
      {
        id: "add_mortgage_manual",
        layout: "full",
        textAlign: "center",
        title: "Add mortgage information",
        subtitle: "Add what you know. We'll help you gather details later.",
        hideButton: true,
        skipOnBack: true,
        customComponent: getAddMortgageForScreen("add_mortgage_manual"),
        shouldDisplay: ({ answers }) =>
          answers["extracted_mortgages"] === "none" &&
          answers["has_mortgage_no_property"] === "yes",
      },
    );
  }

  return screens;
}
